// ============================================================================
// UN RÉGLAGE DE MAGASIN À L'ÉCRAN — sa lecture, son enregistrement, sa ligne dans Paramètres.
//
// UTILITÉ : deux écrans règlent le magasin. « Paramètres » porte le catalogue entier, par onglets ; le
// détail d'une INTÉGRATION porte, dans la carte de son connecteur, les seuls réglages qu'il commande
// (décision utilisateur : un magasin sans wallet n'a pas à traîner un menu inutile dans Paramètres). Les
// deux lisent la même table et écrivent par la même route : la lecture, le brouillon, l'enregistrement
// et les listes servies par un tiers sont écrits ICI une fois — deux copies auraient divergé au premier
// correctif (Forme A).
//
// ⚠️ LA LIGNE (`SettingRow`) EST CELLE DE PARAMÈTRES SEULEMENT : la carte d'un connecteur dessine la sienne
// (le wallet a un menu de gabarits avec pastille de couleur et « déjà pris »), que cette ligne ne sait pas rendre.
//
// ⚠️ LES MESURES DE LA LIGNE SONT CELLES DE LA MAQUETTE DE « PARAMÈTRES » — `parametres-maquette.test.ts`
// les lit dans le dessin et les cherche ici.
// ============================================================================
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Icon, showToast, Tooltip } from '@orgazone/ui-web';
import { formatPhoneForDisplay, normalizePhoneE164 } from '@orgazone/core-crm';
import { isEmailAddress, switchOn, type SwitchName } from '@orgazone/sync-contract';
import { api, type StoreSettingRow } from '../api';
import { GOUVERNANCE, gouvernanceDe, type ReglageParam, type ReglageVue, type SourceListe } from './parametres-modele';
import { servedListFrom, servedListsEffectKey, type ServedList, type ServedLists } from './served-lists';
import { t } from '../i18n';

// ---------------------------------------------------------------------------
// LES LISTES SERVIES PAR UN TIERS
// ---------------------------------------------------------------------------

/**
 * QUI SERT CHAQUE SOURCE. ⚠️ Un `Record` sur l'union des sources : ajouter une source au modèle sans
 * dire ici qui la sert ne compile pas — sinon la nouvelle liste resterait « Chargement… » à jamais.
 */
const LIST_PROVIDERS: Record<SourceListe, () => Promise<ServedList>> = {
  wallet_coupon_templates: () => api.integrationWalletCouponTemplates().then(servedListFrom),
};

/**
 * LES LISTES SERVIES PAR UN TIERS — demandées UNE FOIS, et seulement si un réglage montré en réclame.
 * ⚠️ 🔴 ON NE DEMANDE RIEN QUAND AUCUN RÉGLAGE N'EN A BESOIN : cet appel traverse le connecteur
 *    jusque chez le fournisseur, et le payer à l'ouverture d'un écran qui ne l'affiche pas serait un
 *    aller-retour réseau pour personne. `enabled` = faux tant qu'aucun magasin n'est actif.
 * ⚠️ La dépendance de l'effet est la LISTE DES SOURCES, pas le tableau de réglages : un appelant qui
 *    en construit un neuf à chaque rendu redemanderait sinon la liste à chaque rendu.
 * ⚠️ 🔴 ET `reloadKey` : ELLE SE RELIT QUAND LA CONNEXION SE RELIT — clé posée, test, changement de magasin.
 *    Lue une seule fois, elle affichait l'erreur d'avant la clé à côté d'une connexion « établie ».
 */
export function useServedLists(settings: readonly ReglageVue[], enabled = true, reloadKey = ''): ServedLists {
  const [lists, setLists] = useState<ServedLists>(() => emptyServedLists());
  const effectKey = servedListsEffectKey(settings, enabled, reloadKey);
  useEffect(() => {
    if (!effectKey) return;
    let alive = true;
    const sources = effectKey.slice(0, effectKey.indexOf('|')).split(',') as SourceListe[];
    // Relue : on repasse par « Chargement… » plutôt que de laisser l'ancienne réponse passer pour la nouvelle.
    setLists((p) => { const n = { ...p }; for (const s of sources) n[s] = null; return n; });
    for (const source of sources) {
      LIST_PROVIDERS[source]()
        .catch((e: unknown): ServedList => ({ options: [], error: e instanceof Error ? e.message : String(e) }))
        .then((served) => { if (alive) setLists((p) => ({ ...p, [source]: served })); });
    }
    return () => { alive = false; };
  }, [effectKey]);
  return lists;
}

function emptyServedLists(): ServedLists {
  const empty = {} as ServedLists;
  for (const source of Object.keys(LIST_PROVIDERS) as SourceListe[]) empty[source] = null;
  return empty;
}

// ---------------------------------------------------------------------------
// LE BROUILLON ET SON ENREGISTREMENT
// ---------------------------------------------------------------------------

/**
 * Le brouillon après une saisie.
 * ⚠️ REVENIR À LA VALEUR D'ORIGINE RETIRE LA MODIFICATION : on n'écrit pas au journal un réglage que
 * personne n'a changé — ce serait une révision de plus à descendre sur toute la flotte, pour rien.
 */
export function nextDraft(draft: Record<string, string>, name: string, value: string, origin: string): Record<string, string> {
  const next = { ...draft };
  if (value === origin) delete next[name]; else next[name] = value;
  return next;
}

/** Ce qu'un écran de réglages tient entre deux enregistrements. */
export interface StoreSettingsDraft {
  /** Les lignes servies par le catalogue du magasin — `null` tant qu'elles ne sont pas lues. */
  rows: StoreSettingRow[] | null;
  /** Le motif RÉEL quand elles n'ont pas pu être lues. */
  error: string | null;
  byName: Map<string, StoreSettingRow>;
  draft: Record<string, string>;
  /** Les noms modifiés, non encore enregistrés. */
  changed: string[];
  busy: boolean;
  set: (name: string, value: string, origin: string) => void;
  /** Abandonne le brouillon : chaque réglage revient à ce que le serveur tient. */
  discard: () => void;
  /** Enregistre le brouillon ; vrai s'il est parti, faux s'il a été refusé — le motif est au toast. */
  save: () => Promise<boolean>;
  /**
   * Enregistre CES valeurs tout de suite, sans passer par le brouillon — le geste d'un interrupteur ou
   * d'un choix qui s'applique au clic. Vrai s'il est parti ; le succès se dit avec `success`, le refus
   * avec le motif du serveur.
   */
  commit: (values: Record<string, string>, success: string) => Promise<boolean>;
}

/**
 * LES RÉGLAGES DU MAGASIN, LEUR BROUILLON ET LEUR ENREGISTREMENT — le même chemin pour tout écran.
 * ⚠️ `enabled` = faux tant qu'aucun magasin n'est actif : la route lit la base DU magasin, et
 *    l'interroger sans magasin ne rendrait qu'un refus.
 */
export function useStoreSettingsDraft(enabled = true): StoreSettingsDraft {
  const [rows, setRows] = useState<StoreSettingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    api.storeSettings()
      .then((r) => { if (alive) { setRows(r.settings); setError(null); } })
      .catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [enabled]);

  const byName = useMemo(() => new Map((rows ?? []).map((r) => [r.name, r])), [rows]);
  const changed = Object.keys(draft);
  const set = useCallback((name: string, value: string, origin: string) => setDraft((d) => nextDraft(d, name, value, origin)), []);
  const discard = useCallback(() => setDraft({}), []);

  const save = useCallback(async () => {
    const n = Object.keys(draft).length;
    if (!n || busy) return false;
    setBusy(true);
    try {
      const r = await api.saveStoreSettings(draft);
      setRows(r.settings);
      setDraft({});
      showToast(t('{n} réglage enregistré — il descendra aux caisses au prochain cycle.', { n }), 'ok', 8000);
      return true;
    } catch (e) {
      // ⚠️ LE MOTIF DU SERVEUR, jamais « erreur » : un refus de validation dit QUEL réglage il refuse.
      showToast(t('Enregistrement refusé : {message}', { message: e instanceof Error ? e.message : String(e) }), 'err', 10000);
      return false;
    } finally { setBusy(false); }
  }, [draft, busy]);

  /**
   * ⚠️ L'ÉCRAN NE BASCULE QU'APRÈS LA RÉPONSE : les lignes affichées sont celles que le serveur RENVOIE.
   *    Un interrupteur qui basculerait au clic resterait allumé sur un refus — l'écran annoncerait un
   *    réglage que le serveur n'a pas écrit (§23.0).
   */
  const commit = useCallback(async (values: Record<string, string>, success: string) => {
    if (busy) return false;
    setBusy(true);
    try {
      const r = await api.saveStoreSettings(values);
      setRows(r.settings);
      showToast(success, 'ok', 6000);
      return true;
    } catch (e) {
      showToast(t('Enregistrement refusé : {message}', { message: e instanceof Error ? e.message : String(e) }), 'err', 10000);
      // ⚠️ ON RELIT CE QUE LE SERVEUR TIENT : un refus veut dire qu'un autre a pu écrire entre-temps (le gabarit
      //    qu'on croyait libre), et une panne réseau ne dit pas si l'écriture a eu lieu. Best-effort assumé :
      //    si la relecture échoue aussi, l'écran garde l'état d'avant et le toast a déjà dit l'échec.
      api.storeSettings().then((r) => setRows(r.settings)).catch(() => undefined);
      return false;
    } finally { setBusy(false); }
  }, [busy]);

  return { rows, error, byName, draft, changed, busy, set, discard, save, commit };
}

// ---------------------------------------------------------------------------
// LA LIGNE D'UN RÉGLAGE, ET SES DEUX CONTRÔLES
// ---------------------------------------------------------------------------

/** La pastille des modifications en attente — elle prend la place de l'état des caisses tant que rien n'est enregistré. */
export function PendingChangesPill({ count }: { count: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 34, padding: '0 12px', borderRadius: 'var(--r-pill)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-dense)', fontWeight: 700, whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: 'currentColor' }} />
      {t('{n} modification non enregistrée', { n: count })}
    </span>
  );
}

/**
 * UN INTERRUPTEUR TEL QU'IL S'AFFICHE, et l'origine à laquelle comparer un clic.
 *
 * ⚠️ JAMAIS RÉGLÉ N'EST PAS ÉTEINT : absent, il vaut le défaut que la caisse applique (contrat). L'afficher
 *    éteint montrerait au siège le contraire de ce que fait la caisse.
 * ⚠️ L'ORIGINE EST L'ÉTAT EFFECTIF, écrit comme la caisse l'écrit : revenir à l'état affiché au chargement
 *    retire la modification, au lieu d'inscrire au journal un « 1 » que personne n'a décidé.
 */
export function switchView(name: SwitchName, stored: string | null, input: string | undefined): { on: boolean; origin: string } {
  return { on: switchOn(name, input ?? stored), origin: switchOn(name, stored) ? '1' : '0' };
}

/**
 * Une ligne — relevé : filet EN HAUT, écart 14, colonne d'action à 170 px alignée à droite.
 * ⚠️ LA VALEUR EST UN CHAMP OUVERT, comme le dessin : on vient ici pour régler. La pastille qu'on ouvrait au clic
 *    venait d'un dessin qui ne montrait que la lecture.
 */
export function SettingRow({ view, state, input, onChange }: {
  view: ReglageParam; state: StoreSettingRow;
  /** La saisie en cours (le brouillon) — `undefined` = intacte. */
  input: string | undefined;
  onChange: (name: string, value: string, origin: string) => void;
}) {
  const governance = gouvernanceDe(view.name, state.scope);
  const locked = governance === 'verrouille';
  const modified = input !== undefined;
  const g = GOUVERNANCE[governance];
  const sw = view.forme === 'toggle' ? switchView(view.name, state.value, input) : null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0',
      borderTop: '1px solid var(--ink-100)', flexWrap: 'wrap',
    }}>
      <span style={{ flex: 1, minWidth: 190 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-800)' }}>{t(view.label)}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 19, padding: '0 8px', borderRadius: 'var(--r-pill)', background: g.bg, color: g.fg, fontSize: 'var(--t-eyebrow)', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {t(g.label)}
          </span>
        </span>
        {/* La note du CATALOGUE : elle vit au contrat, l'écran ne la réécrit pas. */}
        <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.5 }}>{t(state.note)}</span>
      </span>

      <span style={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', minWidth: 170 }}>
        {sw ? (
          <>
            {modified && <span style={{ fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--amber-fg)', marginRight: 10, alignSelf: 'center' }}>{t('Modifié')}</span>}
            <SettingSwitch on={sw.on} disabled={locked} label={t(view.label)}
              onToggle={() => onChange(view.name, sw.on ? '0' : '1', sw.origin)} />
          </>
        ) : locked ? (
          <LockedValue view={view} state={state} />
        ) : (
          <SettingInput view={view} state={state} input={input} onChange={onChange} />
        )}
      </span>
    </div>
  );
}

/** Ce qu'affiche une valeur posée : la valeur, ou « défaut de la caisse » quand elle n'a jamais été réglée. */
function shownValue(view: ReglageParam, stored: string | null): string | null {
  if (stored === null || stored === '') return null;
  return view.forme === 'telephone' ? formatPhoneForDisplay(stored) : stored;
}

/** Une valeur verrouillée : lue, jamais saisie, et la raison se dit au survol. */
function LockedValue({ view, state }: { view: ReglageParam; state: StoreSettingRow }) {
  const value = shownValue(view, state.value);
  return (
    <Tooltip label={t('Réglage verrouillé')}
      content={t('Fixé par l’éditeur : il engage la certification ou l’identité fiscale de la caisse, et ne se change pas depuis un magasin.')}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px', borderRadius: 'var(--r-ctrl)',
        background: 'var(--ink-50)', border: '1px solid var(--ink-100)', fontFamily: view.mono ? 'var(--font-mono)' : 'inherit',
        fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-600)', whiteSpace: 'nowrap', maxWidth: '100%',
        overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'not-allowed',
      }}>
        <Icon name="lock" size={12} stroke={2.2} color="var(--ink-500)" style={{ flexShrink: 0 }} />
        {/* ⚠️ « Jamais réglé » ≠ « vide » : la caisse applique son défaut, et l'écran le dit. */}
        {value ?? <span style={{ color: 'var(--ink-400)', fontWeight: 500 }}>{t('défaut de la caisse')}</span>}
      </span>
    </Tooltip>
  );
}

/**
 * CE QU'UNE SAISIE DE TÉLÉPHONE OU D'E-MAIL ENREGISTRERA — la valeur pour le brouillon, et le message sous le champ.
 * ⚠️ LE TÉLÉPHONE PART EN E.164, quel que soit ce qu'on a tapé : la base n'en garde qu'une forme (contrat). Illisible, la
 *    saisie reste au brouillon telle quelle — « Enregistrer » la refuse en la nommant, jamais ne la corrige.
 */
export function readControlledInput(forme: 'telephone' | 'email', typed: string): { value: string; hint: string | null; bad: boolean } {
  const text = typed.trim();
  if (text === '') return { value: '', hint: null, bad: false };
  if (forme === 'email') {
    return isEmailAddress(text) ? { value: text, hint: null, bad: false } : { value: text, hint: t('Adresse e-mail incomplète'), bad: true };
  }
  const e164 = normalizePhoneE164(text);
  return e164
    ? { value: e164, hint: t('Enregistré au format international : {numero}', { numero: e164 }), bad: false }
    : { value: text, hint: t('Numéro illisible — 10 chiffres (03 89 41 22 07) ou indicatif international (+41 …)'), bad: true };
}

/**
 * Le champ d'une valeur — ouvert, aux mesures du dessin, avec « Modifié — non enregistré » et, pour le téléphone et
 * l'e-mail, ce qui sera enregistré ou ce qui cloche.
 * ⚠️ LE TEXTE TAPÉ EST GARDÉ À PART DU BROUILLON pour le téléphone : le brouillon reçoit le E.164, et réécrire le champ
 *    pendant la frappe déplacerait le curseur. Le brouillon retiré (Annuler, Enregistrer), le champ relit la base.
 */
function SettingInput({ view, state, input, onChange }: {
  view: ReglageParam; state: StoreSettingRow; input: string | undefined;
  onChange: (name: string, value: string, origin: string) => void;
}) {
  const hintId = useId();
  const origin = state.value ?? '';
  const [typed, setTyped] = useState<string | null>(null);
  useEffect(() => { if (input === undefined) setTyped(null); }, [input]);
  const controlled = view.forme === 'telephone' || view.forme === 'email' ? view.forme : null;
  const text = typed ?? (view.forme === 'secret' ? (input ?? '') : (shownValue(view, input ?? origin) ?? ''));
  const check = controlled ? readControlledInput(controlled, text) : null;
  const modified = input !== undefined;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, width: 240, maxWidth: '100%' }}>
      <input
        className="oz-setting-input"
        data-modified={modified || undefined}
        type={view.forme === 'secret' ? 'password' : view.forme === 'telephone' ? 'tel' : view.forme === 'email' ? 'email' : 'text'}
        value={text}
        aria-label={t(view.label)}
        aria-invalid={check?.bad || undefined}
        aria-describedby={check?.hint ? hintId : undefined}
        autoComplete={view.forme === 'secret' ? 'new-password' : 'off'}
        spellCheck={false}
        onChange={(e) => {
          const v = e.target.value;
          if (controlled) {
            setTyped(v);
            onChange(view.name, readControlledInput(controlled, v).value, origin);
          } else onChange(view.name, v, view.forme === 'secret' ? '' : origin);
        }}
        placeholder={view.forme === 'secret' ? (state.pose ? t('•••••••• (enregistré)') : t('aucun mot de passe')) : state.value === null ? t('défaut de la caisse') : ''}
        style={{
          width: '100%', height: 34, padding: '0 11px', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
          fontFamily: view.mono ? 'var(--font-mono)' : 'inherit', fontSize: 'var(--t-lead)', fontWeight: 600,
          color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box',
        }} />
      {modified && <span style={{ fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--amber-fg)' }}>{t('Modifié — non enregistré')}</span>}
      {check?.hint && (
        <span id={hintId} style={{ fontSize: 'var(--t-meta)', fontWeight: 600, color: check.bad ? 'var(--rose-fg)' : 'var(--ink-500)', textAlign: 'right', lineHeight: 1.35 }}>
          {check.hint}
        </span>
      )}
    </span>
  );
}

/** L'interrupteur du dessin : 38×22, curseur de 16 à 19 px (actif) ou 3 px (repos). */
function SettingSwitch({ on, disabled, onToggle, label }: {
  on: boolean; disabled: boolean; onToggle: () => void; label: string;
}) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label}
      onClick={disabled ? undefined : onToggle} disabled={disabled}
      style={{
        position: 'relative', width: 38, height: 22, border: 'none', padding: 0,
        borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-500)' : 'var(--ink-300)',
        transition: 'background .15s', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}>
      <span style={{
        position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16,
        borderRadius: 'var(--r-pill)', background: '#fff',
        boxShadow: '0 1px 3px rgba(12,14,16,.3)', transition: 'left .15s',
      }} />
    </button>
  );
}
